<template>
  <div class="class-diagram">
    <!-- O wheel precisa ser passivo=false para o preventDefault valer e a página não
         rolar junto com o zoom; por isso ele é registrado à mão, e não no template.

         A altura do desenho vai como VARIÁVEL, e não como `height`: a caixa a usa para caber o
         desenho inteiro (ver o estilo), e a tela cheia continua podendo trocar a altura pelo CSS —
         um `height` aqui ganharia da regra dela. -->
    <div ref="viewport" class="class-diagram-viewport"
         :class="{'is-panning': panning}" :style="{'--diagram-height': `${shownHeight}px`}"
         @pointerdown="onPointerDown" @pointermove="onPointerMove"
         @pointerup="onPointerUp" @pointercancel="onPointerUp">
      <div class="class-diagram-canvas" :style="canvasStyle">
        <svg class="class-diagram-links" :width="diagram.width" :height="diagram.height">
          <defs>
            <marker id="class-diagram-arrow" markerWidth="10" markerHeight="8"
                    refX="9" refY="4" orient="auto">
              <path d="M0,0 L10,4 L0,8" fill="none" stroke="#546e7a" stroke-width="1.4"/>
            </marker>
            <!-- Marcador próprio para a ponta de saída, com o desenho invertido: o
                 orient="auto-start-reverse" que faria isso sozinho é SVG 2 e não vale
                 em todo navegador que o projeto atende. -->
            <marker id="class-diagram-arrow-start" markerWidth="10" markerHeight="8"
                    refX="1" refY="4" orient="auto">
              <path d="M10,0 L0,4 L10,8" fill="none" stroke="#546e7a" stroke-width="1.4"/>
            </marker>
            <!-- Diamante cheio: composição. Encosta pela ponta na caixa do dono, e por
                 isso o refX fica na extremidade que toca a borda — 14 no fim da linha,
                 0 no começo dela. -->
            <marker id="class-diagram-diamond" markerWidth="14" markerHeight="10"
                    refX="14" refY="5" orient="auto">
              <path d="M0,5 L7,0 L14,5 L7,10 Z" fill="#546e7a" stroke="#546e7a" stroke-width="1"/>
            </marker>
            <marker id="class-diagram-diamond-start" markerWidth="14" markerHeight="10"
                    refX="0" refY="5" orient="auto">
              <path d="M0,5 L7,0 L14,5 L7,10 Z" fill="#546e7a" stroke="#546e7a" stroke-width="1"/>
            </marker>
          </defs>
          <g v-for="edge of drawnEdges" :key="`edge-${edge.id}`" class="class-diagram-edge"
             @click="onEdgeClick(edge)">
            <title>{{ edge.title }}</title>
            <!-- Área de acerto: mesmos pontos, traço grosso e transparente. Mirar 1,4px
                 com o ponteiro é sofrido, e com o dedo é impossível. -->
            <polyline class="class-diagram-hit" :points="pointsOf(edge)"
                      fill="none" stroke="transparent" stroke-width="14"/>
            <!-- Ponta nos dois extremos quando a ligação é navegável dos dois lados; uma
                 só quando existe apenas de um. Na ponta do dono o diamante entra no lugar
                 da seta: em UML os dois não se acumulam no mesmo extremo. -->
            <polyline class="class-diagram-line" :points="pointsOf(edge)"
                      fill="none" stroke="#546e7a" stroke-width="1.4"
                      :marker-start="markerAt(edge, 'source')"
                      :marker-end="markerAt(edge, 'target')"/>
            <text class="class-diagram-label" v-bind="labelAt(edge, 'source')">
              {{ edge.sourceLabel }}
            </text>
            <text class="class-diagram-label" v-bind="labelAt(edge, 'target')">
              {{ edge.targetLabel }}
            </text>
          </g>
        </svg>

        <!-- Duas naturezas de caixa no mesmo espaço de coordenadas: a coleção e o domínio
             de um atributo de seleção. O layout não as distingue — para ele são alias e
             altura —, e é aqui que cada uma vira o componente que a desenha. -->
        <template v-for="box of drawnBoxes" :key="box.alias">
          <ontos-class-box v-if="box.content.kind === 'class'"
                           class="class-diagram-box" :style="boxStyle(box)"
                           :model="box.content.model" :can-write="canWrite"
                           @open="onOpen({...$event, idea: box.content.model.alias})"/>
          <ontos-domain-box v-else
                            class="class-diagram-box" :style="boxStyle(box)"
                            :domain="box.content.domain"
                            @open="onOpen({kind: 'domain', domain: box.content.domain.alias})"/>
        </template>
      </div>
    </div>

    <!-- Os controles DENTRO da caixa do desenho, numa coluna no canto, como o zoom dos mapas
         (escolha do usuário). Eram um grupo no cabeçalho da aba, a única aba com uma segunda linha
         de botões, e falavam do desenho longe dele. Irmãos da viewport, e não filhos: lá dentro o
         toque num botão começaria um arrasto.

         Lupas, e não + e −: o "+" é o de criar coleção, logo acima, e dois botões iguais com
         sentidos diferentes confundem. A tela cheia só onde ela existe — no próprio diagrama em
         tela cheia, ele não se expande. -->
    <div class="class-diagram-controls">
      <v-btn v-for="control of controls" :key="control.label" variant="text" size="small"
             density="comfortable" :icon="control.icon" :title="control.label" :aria-label="control.label"
             @click="control.act()"/>
    </div>

    <div class="class-diagram-legend">
      <span v-for="entry of legend" :key="entry.label" class="class-diagram-legend-item">
        <v-icon size="x-small" :color="entry.color">{{ entry.icon }}</v-icon>{{ entry.label }}
      </span>
    </div>
  </div>
</template>
<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from 'vue-facing-decorator'
import ClassBox from '@/components/design/system/ClassBox.vue'
import DomainBox from '@/components/design/system/DomainBox.vue'
import {EnhancedWorkpace} from '@/types/Workspace'
import {ClassEdge, DiagramBox, DiagramTarget, classDiagramModel} from '@/utils/classModel'
import {Diagram, LaidOutBox, LaidOutEdge, Orientation, layoutDiagram, orientationFor} from '@/utils/classDiagram'
import {Transform, anchoredTransform, boundedTransform, clampScale} from '@/utils/viewport'
import {watchSize} from '@/utils/elementSize'

type Point = { x: number; y: number }

/** A aresta posicionada, já com o que ela significa: rótulos, dono e o texto do title. */
type DrawnEdge = LaidOutEdge & {
  sourceLabel: string
  targetLabel: string
  bidirectional: boolean
  owner: 'source' | 'target' | null
  title: string
}

/** Quanto o clique de zoom multiplica a escala. */
const STEP = 1.25
const MIN_SCALE = 0.25
const MAX_SCALE = 2
/** Acima disto o ponteiro andou: é arrasto, não clique. */
const DRAG_SLOP = 4

/**
 * O diagrama: linhas em SVG por baixo, caixas em HTML por cima, no mesmo espaço de
 * coordenadas.
 *
 * HTML e não foreignObject para as caixas — o foreignObject não é confiável para
 * reticências, foco e clique entre navegadores, e perderia o estilo do Vuetify. Como as
 * duas camadas vivem dentro do mesmo elemento transformado, pan e zoom valem para as
 * duas de uma vez, e não há um segundo sistema de coordenadas para manter em sincronia.
 */
@Component({
  emits: ['open', 'expand'],
  name: 'ontos-class-diagram',
  components: {'ontos-class-box': ClassBox, 'ontos-domain-box': DomainBox}
})
class ClassDiagram extends Vue {
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace
  /** Oferece a tela cheia (`expand`)? Não no próprio diagrama em tela cheia. */
  @Prop({type: Boolean, default: false}) readonly expandable!: boolean

  /** Os controles da coluna do canto, de cima para baixo — ver o template. */
  get controls(): { icon: string, label: string, act: () => void }[] {
    return [
      {icon: 'mdi-magnify-plus-outline', label: this.$tt('diagram.zoom-in') as string, act: () => this.zoomIn()},
      {icon: 'mdi-magnify-minus-outline', label: this.$tt('diagram.zoom-out') as string, act: () => this.zoomOut()},
      {icon: 'mdi-fit-to-screen-outline', label: this.$tt('diagram.fit') as string, act: () => this.fit()},
      ...(this.expandable ? [{icon: 'mdi-arrow-expand-all', label: this.$tt('diagram.fullscreen') as string,
        act: () => this.$emit('expand')}] : [])
    ]
  }

  /**
   * O diagrama aceita alteração? Num ambiente encerrado — ou para quem não desenha — ele é só
   * leitura: clicar numa caixa, numa propriedade ou numa linha não abre editor nenhum, e o
   * rótulo deixa de prometer "Editar".
   *
   * Sai do próprio ambiente, que já chega aqui por prop — ver o `can` do `Workspace`. Mantém o
   * nome local `canWrite` porque é isto que a `ClassBox` recebe: lá a pergunta é "desenho meus
   * alvos de clique?", e qual nível respondeu a ela é assunto de quem hospeda.
   */
  get canWrite(): boolean {
    return this.workspace.can('designer')
  }

  scale = 1
  tx = 0
  ty = 0
  panning = false

  /**
   * Largura útil da viewport, observada. É ela que escolhe a orientação, e não o
   * breakpoint do Vuetify: o diagrama também aparece dentro de um modal, onde o espaço
   * disponível não tem relação com o tamanho da janela.
   */
  viewportWidth = 0

  //Estado do arrasto em curso. Fora do data: nada aqui é lido pelo template, e cada
  //movimento do ponteiro dispararia um ciclo de reatividade à toa.
  private origin = {x: 0, y: 0, tx: 0, ty: 0}
  private travelled = 0
  private captured = false

  /** Dedos encostados agora. Dois deles são a pinça; um só é arrasto. */
  private pointers: Map<number, { x: number; y: number }> = new Map()
  /** Estado do INÍCIO da pinça — o que fixa a escala e o ponto sob os dedos. */
  private pinch: { distance: number; midpoint: Point; scale: number; tx: number; ty: number } | null = null
  /** Desfaz a observação de tamanho; null enquanto não há nenhuma. */
  private stopSizeWatch: (() => void) | null = null

  /**
   * As caixas e as ligações, de uma chamada só: a caixa de domínio existe porque uma
   * ligação chega nela, e quem decide as duas coisas é o mesmo lugar.
   */
  get content(): { boxes: DiagramBox[]; edges: ClassEdge[] } {
    return classDiagramModel(this.workspace)
  }

  get edges(): ClassEdge[] {
    return this.content.edges
  }

  /**
   * Camadas da esquerda para a direita só enquanto couberem duas lado a lado. Num
   * telefone não cabem: o desenho ficava largo demais para a única dimensão escassa, e
   * ver tudo exigia um zoom que tornava as caixas ilegíveis. Empilhado, cada caixa ocupa
   * a largura inteira e o que cresce é a altura — que rola.
   */
  get orientation(): Orientation {
    return orientationFor(this.viewportWidth)
  }

  get diagram(): Diagram {
    const nodes = this.content.boxes.map(box => ({alias: box.alias, height: box.height}))
    //A entidade forte primeiro: quando o destino é o dono, é a identidade da ORIGEM que
    //depende dele, então ele precisa estar desenhado antes de ela aparecer
    const links = this.edges.map((edge, id) => ({
      from: edge.from, to: edge.to, id,
      fromAnchor: edge.sourceAnchor, toAnchor: edge.targetAnchor,
      first: edge.owner === 'target' ? 'to' as const : 'from' as const
    }))
    return layoutDiagram(nodes, links, this.orientation)
  }

  /**
   * A geometria e o que ela significa, juntas.
   *
   * São duas listas porque o layout não conhece domínio: ele devolve pontos, e os
   * rótulos de multiplicidade vêm do classEdges. O casamento é pelo id, e não pela
   * posição na lista, porque o layout descarta aresta órfã — casar por posição
   * desalinharia rótulo e linha em silêncio.
   */
  get drawnEdges(): DrawnEdge[] {
    return this.diagram.edges.map(edge => {
      //link e não source: `edge.source` já é um PONTO, e os dois nomes juntos confundem
      const link = this.edges[edge.id]
      const owns = link.owner === 'source' ? link.from : link.owner === 'target' ? link.to : ''
      const composition = owns ? ` · ${this.$tt('diagram.owned-by', {collection: this.nameOf(owns)})}` : ''
      //A ligação com um domínio não tem rótulo na origem — o domínio não sabe quem o usa.
      //Sem isto o title abriria com um espaço solto antes da seta.
      const arrow = link.bidirectional ? '↔' : '→'
      const cardinality = link.sourceLabel
          ? `${link.sourceLabel} ${arrow} ${link.targetLabel}`
          : `${arrow} ${link.targetLabel}`
      return {
        ...edge,
        sourceLabel: link.sourceLabel,
        targetLabel: link.targetLabel,
        bidirectional: link.bidirectional,
        owner: link.owner,
        title: this.canWrite
            ? `${link.label}: ${cardinality}${composition} · ${this.$tt('edit')}`
            : `${link.label}: ${cardinality}${composition}`
      }
    })
  }

  get legend() {
    return [
      {icon: 'mdi-key', color: 'amber-darken-3', label: this.$tt('definition.identifier')},
      {icon: 'mdi-asterisk', color: 'red-darken-1', label: this.$tt('required')},
      {icon: 'mdi-fingerprint', color: 'blue-grey', label: this.$tt('unique')},
      {icon: 'mdi-function-variant', color: 'purple', label: this.$tt('computed')},
      {icon: 'mdi-format-list-bulleted', color: 'orange-darken-4', label: this.$tt('domain')},
      {icon: 'mdi-arrow-right', color: 'teal', label: this.$tt('diagram.one-way')},
      {icon: 'mdi-arrow-left-right', color: 'teal', label: this.$tt('diagram.two-way')},
      {icon: 'mdi-rhombus', color: 'blue-grey-darken-2', label: this.$tt('diagram.composition')}
    ]
  }

  /**
   * A altura que a caixa dá ao desenho: a dele na escala de agora, até a natural. Afastar o zoom — ou
   * ajustar, que no telefone encolhe o desenho até caber na largura — encolhe a caixa junto (escolha
   * do usuário): com a altura fixa no tamanho natural, o desenho ajustado ficava miúdo no meio de uma
   * caixa vazia. Aproximar além do natural não a faz crescer: ali se arrasta.
   */
  get shownHeight(): number {
    return this.diagram.height * Math.min(this.scale, 1)
  }

  get canvasStyle() {
    return {
      width: `${this.diagram.width}px`,
      height: `${this.diagram.height}px`,
      transform: `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`
    }
  }

  mounted() {
    const viewport = this.$refs.viewport as HTMLElement
    //passive: false porque o preventDefault do wheel só vale assim
    viewport.addEventListener('wheel', this.onWheel, {passive: false})
    //A viewport, e não a janela: ela encolhe também quando a gaveta de navegação abre
    //ou a página se reacomoda, sem redimensionar janela nenhuma.
    this.stopSizeWatch = watchSize(viewport, () => this.measure())
    this.measure()
    this.frame()
  }

  beforeUnmount() {
    const viewport = this.$refs.viewport as HTMLElement | undefined
    if (viewport) viewport.removeEventListener('wheel', this.onWheel)
    this.stopSizeWatch?.()
  }

  /**
   * Encolher a viewport pode deixar caixas fora dela, e o limite só era aplicado quando
   * alguém mexia no desenho — as caixas ficavam para fora sem nada trazê-las de volta.
   * Reaplicar a posição atual pelo limite resolve: ela se ajusta ao tamanho novo.
   */
  measure() {
    const viewport = this.$refs.viewport as HTMLElement | undefined
    if (!viewport) return
    this.viewportWidth = viewport.clientWidth
    this.applyTransform(this.transform)
  }

  //Girar o telefone pode trocar a orientação do desenho, e o enquadramento de antes
  //deixaria de fazer sentido
  @Watch('orientation')
  onOrientationChanged() {
    this.$nextTick(() => this.frame())
  }

  //Coleção criada ou excluída muda o tamanho do desenho; o enquadramento de antes
  //deixaria a nova caixa fora da vista
  @Watch('diagram.width')
  @Watch('diagram.height')
  onSizeChanged() {
    this.$nextTick(() => this.frame())
  }

  /**
   * Índice em vez de find no template: arrastar o diagrama muda tx/ty, o que
   * re-renderiza o componente a cada movimento do ponteiro. Com busca linear dentro do
   * v-for isso seria quadrático por quadro; e como o índice devolve sempre a MESMA
   * referência, as caixas não re-renderizam junto — só o transform da tela muda.
   */
  get boxesByAlias(): Record<string, DiagramBox> {
    const index: Record<string, DiagramBox> = {}
    this.content.boxes.forEach(box => { index[box.alias] = box })
    return index
  }

  /**
   * A caixa posicionada e o que ela mostra, juntas — pelo mesmo motivo que as arestas:
   * o layout devolve geometria, e o significado vem do outro lado, casado por alias.
   */
  get drawnBoxes(): Array<LaidOutBox & { content: DiagramBox }> {
    return this.diagram.boxes.map(box => ({...box, content: this.boxesByAlias[box.alias]}))
  }

  nameOf(alias: string): string {
    const box = this.boxesByAlias[alias]
    if (!box) return alias
    return box.kind === 'class' ? box.model.name : box.domain.name
  }

  boxStyle(box: LaidOutBox) {
    return {left: `${box.x}px`, top: `${box.y}px`, height: `${box.height}px`}
  }

  pointsOf(edge: LaidOutEdge): string {
    return edge.points.map(point => `${point.x},${point.y}`).join(' ')
  }

  /**
   * O que fica em cada ponta da linha.
   *
   * O diamante do dono vence a seta: em UML os dois não se acumulam no mesmo extremo, e
   * o diamante já é a informação mais forte ali. A seta de saída só aparece quando a
   * ligação é navegável dos dois lados; a de chegada é o sentido da própria ligação.
   */
  markerAt(edge: DrawnEdge, end: 'source' | 'target'): string | undefined {
    if (edge.owner === end) {
      return end === 'source' ? 'url(#class-diagram-diamond-start)' : 'url(#class-diagram-diamond)'
    }
    if (end === 'target') return 'url(#class-diagram-arrow)'
    return edge.bidirectional ? 'url(#class-diagram-arrow-start)' : undefined
  }

  /**
   * Rótulo de multiplicidade junto à ponta da linha, sempre do lado de FORA da caixa.
   *
   * `outward` é o sentido em que a linha se afasta da caixa naquela ponta: na origem ela
   * sai, no destino ela chega, por isso os dois se invertem. Sem isso, numa aresta que
   * volta para trás o texto cairia por cima da caixa.
   *
   * Com as camadas empilhadas a linha sai por baixo e entra por cima, então o
   * afastamento é vertical e o texto fica ao lado; na horizontal é o contrário.
   */
  labelAt(edge: LaidOutEdge, end: 'source' | 'target') {
    const point = edge[end]
    const outward = edge.forward === (end === 'source') ? 1 : -1
    if (this.diagram.orientation === 'vertical') {
      //dy acompanha o sentido para o texto não encostar na borda de onde a linha sai
      return {x: point.x + 6, y: point.y + outward * 13, 'text-anchor': 'start'}
    }
    return {x: point.x + outward * 6, y: point.y - 5, 'text-anchor': outward > 0 ? 'start' : 'end'}
  }

  /**
   * Enquadramento de entrada: o desenho no TAMANHO NATURAL, a partir do canto.
   *
   * Encaixar tudo na tela seria pedir para caber a dimensão mais longa, e num diagrama
   * grande isso dá um zoom em que nada se lê. Ajustar pela largura também não servia, e
   * de um jeito pior: `largura × escala` ficava exatamente igual à da viewport, o limite
   * via "cabe inteiro" e travava o arrasto — o diagrama nascia sem rolagem lateral.
   *
   * No tamanho natural a caixa sai legível e sobra desenho para os lados, que é o que o
   * arrasto alcança. Ver tudo de uma vez é o que o botão de ajustar faz.
   */
  frame() {
    this.reframe('natural')
  }

  /** O botão: mostrar tudo de uma vez, custe o zoom que custar. */
  fit() {
    this.reframe('all')
  }

  /**
   * Centraliza o desenho com a escala que a regra pedir.
   *
   * Tenta de novo no quadro seguinte enquanto a viewport não tiver tamanho: dentro do
   * diálogo de tela cheia o mounted acontece antes de a transição dar altura ao
   * elemento, e a conta sairia com zero. O teto de tentativas evita laço infinito se a
   * tela for fechada antes de abrir.
   *
   * A posição é calculada DEPOIS de a escala nova chegar à caixa, que acompanha o zoom (ver o
   * `shownHeight`): centrando pela altura de antes, o desenho ajustado caía no meio de baixo da
   * caixa que ainda ia encolher.
   */
  private async reframe(mode: 'natural' | 'all', attempt = 0) {
    const viewport = this.$refs.viewport as HTMLElement | undefined
    const {width, height} = this.diagram
    if (!viewport || !width || !height) return
    if (!viewport.clientWidth || !viewport.clientHeight) {
      if (attempt < 10) requestAnimationFrame(() => this.reframe(mode, attempt + 1))
      return
    }
    const before = this.viewSize()
    //Nunca amplia além de 1: um diagrama de duas caixas esticado até a borda fica grotesco
    const wanted = mode === 'all' ? Math.min(before.width / width, before.height / height, 1) : 1
    const scale = Math.max(wanted, MIN_SCALE)
    this.scale = scale
    await this.$nextTick()
    //Medidas de depois da espera: no meio dela a orientação pode ter mudado o desenho
    const view = this.viewSize()
    const drawn = {width: this.diagram.width * scale, height: this.diagram.height * scale}
    //Na horizontal, SEMPRE centrado, caiba ou não: as camadas do desenho são centradas na mais
    //larga, e encostado à esquerda o telefone nascia mostrando o vão ao lado da primeira coleção,
    //com ela cortada na borda direita (relato do usuário). Na vertical, centrado se cabe e do topo se
    //não cabe: a leitura começa no começo, e o resto se alcança arrastando.
    this.applyTransform({
      scale,
      tx: (view.width - drawn.width) / 2,
      ty: drawn.height <= view.height ? (view.height - drawn.height) / 2 : 0
    })
  }

  /** O que a coluna de controles do canto chama — ver o `controls`. */
  zoomIn() {
    this.zoomBy(STEP)
  }

  zoomOut() {
    this.zoomBy(1 / STEP)
  }

  /** Zoom pelo centro da viewport, que é o que o botão significa. */
  zoomBy(factor: number) {
    const viewport = this.$refs.viewport as HTMLElement
    this.zoomAt(factor, viewport.clientWidth / 2, viewport.clientHeight / 2)
  }

  private get transform(): Transform {
    return {scale: this.scale, tx: this.tx, ty: this.ty}
  }

  /**
   * MÉTODO, e nunca um getter: getter aqui vira computed, e computed só reavalia quando
   * uma dependência REATIVA muda. O tamanho da viewport vem do DOM, que não é reativo —
   * como getter isto era medido uma vez no mount e ficava em cache para sempre, e os
   * limites continuavam presos ao tamanho de então por mais que a janela mudasse.
   */
  private viewSize() {
    const viewport = this.$refs.viewport as HTMLElement | undefined
    return {width: viewport ? viewport.clientWidth : 0, height: viewport ? viewport.clientHeight : 0}
  }

  /**
   * Passa por aqui todo deslocamento, venha de arrasto, pinça, roda ou botão. É o único
   * lugar que escreve a posição, e por isso o único que precisa conhecer o limite.
   */
  private applyTransform(next: Transform) {
    const bounded = boundedTransform(next, this.diagram, this.viewSize())
    this.scale = bounded.scale
    this.tx = bounded.tx
    this.ty = bounded.ty
  }

  /** Mantém fixo o ponto sob o cursor: é o que faz o zoom parecer preso ao desenho. */
  zoomAt(factor: number, x: number, y: number) {
    const wanted = clampScale(this.scale * factor, MIN_SCALE, MAX_SCALE)
    //Âncora igual antes e depois: o cursor não anda durante o passo de zoom
    this.applyTransform(anchoredTransform(this.transform, wanted, {x, y}, {x, y}))
  }

  /**
   * Zoom só com Ctrl, como nos mapas embutidos. Sem ele a roda é da PÁGINA: a caixa tem a altura
   * do desenho inteiro, e num diagrama grande ela pode ocupar a tela toda — a roda presa no zoom
   * deixaria sem jeito de rolar para além dele. A pinça do trackpad chega como roda com Ctrl, e
   * continua dando zoom.
   */
  onWheel(event: WheelEvent) {
    if (!event.ctrlKey) return
    event.preventDefault()
    const viewport = this.$refs.viewport as HTMLElement
    const bounds = viewport.getBoundingClientRect()
    this.zoomAt(event.deltaY < 0 ? STEP : 1 / STEP, event.clientX - bounds.left, event.clientY - bounds.top)
  }

  /** Do ponto da tela para o ponto dentro da viewport, que é o sistema do transform. */
  private localPoint(clientX: number, clientY: number) {
    const bounds = (this.$refs.viewport as HTMLElement).getBoundingClientRect()
    return {x: clientX - bounds.left, y: clientY - bounds.top}
  }

  private activePoints() {
    return Array.from(this.pointers.values())
  }

  /**
   * Começa a pinça guardando o estado do gesto, e não o de cada quadro.
   *
   * A escala sai da razão entre a distância de agora e a do INÍCIO; medir a cada quadro
   * acumularia o arredondamento e o desenho escorregaria sob os dedos.
   */
  private beginPinch(viewport: HTMLElement) {
    const [a, b] = this.activePoints()
    this.pinch = {
      distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
      midpoint: this.localPoint((a.x + b.x) / 2, (a.y + b.y) / 2),
      scale: this.scale, tx: this.tx, ty: this.ty
    }
    //A pinça não é clique, e o arrasto de um dedo só para aqui
    this.panning = false
    this.travelled = DRAG_SLOP + 1
    this.pointers.forEach((point, id) => {
      if (viewport.hasPointerCapture(id)) viewport.releasePointerCapture(id)
    })
    this.captured = false
  }

  /** Mantém sob os dedos o mesmo ponto do desenho que estava lá quando a pinça começou. */
  private applyPinch() {
    if (!this.pinch) return
    const [a, b] = this.activePoints()
    const distance = Math.hypot(a.x - b.x, a.y - b.y) || 1
    const midpoint = this.localPoint((a.x + b.x) / 2, (a.y + b.y) / 2)
    const wanted = clampScale(this.pinch.scale * (distance / this.pinch.distance), MIN_SCALE, MAX_SCALE)
    //Âncoras diferentes: o meio dos dedos anda, então a pinça move o desenho junto com o
    //zoom. E a referência é o estado do INÍCIO do gesto, não o do quadro anterior.
    this.applyTransform(anchoredTransform(this.pinch, wanted, this.pinch.midpoint, midpoint))
  }

  onPointerDown(event: PointerEvent) {
    //Só o botão principal: o secundário abre o menu do navegador e nunca recebe o pointerup
    if (event.button !== 0) return
    this.pointers.set(event.pointerId, {x: event.clientX, y: event.clientY})

    if (this.pointers.size === 2) {
      this.beginPinch(event.currentTarget as HTMLElement)
      return
    }
    if (this.pointers.size > 2) return

    this.panning = true
    this.travelled = 0
    this.captured = false
    this.origin = {x: event.clientX, y: event.clientY, tx: this.tx, ty: this.ty}
  }

  /**
   * A captura do ponteiro só entra DEPOIS que o arrasto passa da folga, e não já no
   * pointerdown.
   *
   * Com a captura ativa o pointerup é despachado em quem capturou, e o click resultante
   * vai para o ancestral comum — a viewport, não a caixa. Capturando no pointerdown,
   * portanto, um clique numa classe nunca chegaria ao @click.native dela e o modal não
   * abriria. Capturando só no arrasto de verdade, o clique parado segue o caminho normal
   * e o arrasto continua funcionando com o ponteiro fora da viewport.
   */
  onPointerMove(event: PointerEvent) {
    const tracked = this.pointers.get(event.pointerId)
    if (!tracked) return
    tracked.x = event.clientX
    tracked.y = event.clientY

    if (this.pointers.size >= 2) {
      this.applyPinch()
      return
    }
    if (!this.panning) return

    const dx = event.clientX - this.origin.x
    const dy = event.clientY - this.origin.y
    this.travelled = Math.max(this.travelled, Math.abs(dx) + Math.abs(dy))
    if (!this.captured && this.travelled > DRAG_SLOP) {
      this.captured = true
      ;(event.currentTarget as HTMLElement).setPointerCapture(event.pointerId)
    }
    //A partir do dx absoluto, e não somando quadro a quadro: assim voltar o ponteiro
    //volta o desenho, mesmo depois de ter esbarrado no limite
    this.applyTransform({scale: this.scale, tx: this.origin.tx + dx, ty: this.origin.ty + dy})
  }

  onPointerUp(event: PointerEvent) {
    if (!this.pointers.delete(event.pointerId)) return
    const target = event.currentTarget as HTMLElement
    if (this.captured && target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId)
    }
    this.captured = false
    this.pinch = null

    if (this.pointers.size === 1) {
      /**
       * Soltou um dedo no meio da pinça: o que ficou passa a arrastar, e o ponto de
       * partida tem de ser o DELE. Reaproveitar a origem de antes faria o desenho saltar
       * para a distância entre os dois dedos.
       *
       * O percurso já conta como andado: levantar esse dedo parado não pode abrir a
       * classe que estiver por baixo.
       */
      const [remaining] = this.activePoints()
      this.panning = true
      this.travelled = DRAG_SLOP + 1
      this.origin = {x: remaining.x, y: remaining.y, tx: this.tx, ty: this.ty}
      return
    }
    if (!this.pointers.size) this.panning = false
  }

  /**
   * Arrastar sobre o desenho move o diagrama; só o clique parado abre algo.
   *
   * Todo alvo passa por aqui — cabeçalho, propriedade, fluxo e linha — porque só o
   * diagrama sabe quanto o ponteiro andou. A caixa e a linha apenas dizem o que foi
   * clicado.
   */
  onOpen(target: DiagramTarget) {
    if (this.travelled > DRAG_SLOP) return
    //Encerrado, só a COLEÇÃO continua sendo alvo: dela há o que mostrar, e quem decide o que
    //fazer com o alvo é quem hospeda o diagrama. Propriedade, fluxo e linha abririam editor, e
    //ali não há o que editar.
    if (!this.canWrite && target.kind !== 'class') return
    this.$emit('open', target)
  }

  /** A linha leva à propriedade que a origina — a direta, nunca o espelho. */
  onEdgeClick(edge: DrawnEdge) {
    const link = this.edges[edge.id]
    this.onOpen({kind: 'property', property: link.alias, idea: link.from})
  }
}

export default toNative(ClassDiagram)
</script>
<style scoped>
.class-diagram {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* A coluna dos controles no canto de cima da caixa do desenho — a caixa é o primeiro filho, então o
   canto do diagrama é o dela —, 8px para dentro da borda. Um cartão branco de cantos arredondados e
   sombra leve, o desenho dos controles de zoom dos mapas: fica sobre o desenho sem se confundir com
   ele, e não se mexe quando o desenho é arrastado por baixo. */
.class-diagram-controls {
  position: absolute;
  top: 9px;
  right: 9px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
}

/* A altura do desenho inteiro na escala de agora, até a natural (ver o `shownHeight`), e não uma
   fração da janela: com `clamp(320px, 60vh, 720px)` o diagrama alto nascia cortado embaixo (pedido do
   usuário: a caixa sempre mostra o desenho todo). O mínimo é para o diagrama de uma ou duas
   coleções não virar uma faixa fina; os 2px são a borda, que o `border-box` desconta da área útil —
   sem eles o desenho passava 2px da caixa e não centralizava.

   touch-action: pan-y porque a caixa pode ser mais alta que a tela, sobretudo no telefone, com as
   caixas empilhadas: um dedo na vertical rola a PÁGINA, e na horizontal arrasta o desenho. Com
   `none`, como era, não havia como rolar para além do diagrama. A pinça continua sendo do
   diagrama: `pinch-zoom` fica de fora, e o navegador não a toma. */
.class-diagram-viewport {
  position: relative;
  overflow: hidden;
  height: max(320px, calc(var(--diagram-height) + 2px));
  border: 1px solid rgba(0, 0, 0, .12);
  border-radius: 4px;
  background: #fafafa;
  touch-action: pan-y;
  cursor: grab;
}

.class-diagram-viewport.is-panning {
  cursor: grabbing;
}

/* transform-origin no canto: com o padrão (centro) a conta do zoom sob o cursor teria
   de compensar metade do tamanho do desenho a cada passo. */
.class-diagram-canvas {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: 0 0;
}

/* pointer-events: none na camada e stroke só na área de acerto: o <svg> é um retângulo
   do tamanho do desenho inteiro, por cima do canvas, e sem isso ele interceptaria o
   ponteiro em toda a área vazia entre as linhas. */
.class-diagram-links {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}

.class-diagram-hit {
  pointer-events: stroke;
  cursor: pointer;
}

/* O realce vai na linha VISÍVEL, acionado pelo hover do grupo — é a área de acerto que
   recebe o ponteiro, e ela é invisível. */
.class-diagram-edge:hover .class-diagram-line {
  stroke: var(--v-primary-base, #1976d2);
  stroke-width: 2.4;
}

.class-diagram-box {
  position: absolute;
}

/* Halo branco por baixo do texto: o rótulo cai sobre a linha, e sem ele os dois se
   misturam justamente onde a leitura importa. */
.class-diagram-label {
  font-size: 11px;
  fill: #37474f;
  paint-order: stroke;
  stroke: #fafafa;
  stroke-width: 3px;
  stroke-linejoin: round;
}

.class-diagram-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, .6);
}

.class-diagram-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
</style>
